/**
 * Pure helpers for the project Docs tab: the page tree, and the formatting
 * actions of the editor toolbar. No React, so both are unit-tested directly.
 */

export type DocPageSummary = {
  id: string;
  parentId: string | null;
  position: number;
  title: string;
  version: number;
  updatedAt: string;
  lastEditedByName: string | null;
};

export type DocNode<T extends DocPageSummary = DocPageSummary> = { page: T; depth: number; children: DocNode<T>[] };

/**
 * The flat list as a forest, siblings ordered by position then title. A page
 * whose parent is missing from the list (not loaded, or deleted) is shown at
 * the top level rather than lost.
 */
export function buildDocTree<T extends DocPageSummary>(pages: T[]): DocNode<T>[] {
  const ids = new Set(pages.map((p) => p.id));
  const byParent = new Map<string | null, T[]>();
  for (const page of pages) {
    const key = page.parentId && ids.has(page.parentId) ? page.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), page]);
  }
  const build = (parent: string | null, depth: number, seen: Set<string>): DocNode<T>[] =>
    (byParent.get(parent) ?? [])
      .slice()
      .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title))
      .filter((p) => !seen.has(p.id))
      .map((page) => ({
        page,
        depth,
        children: build(page.id, depth + 1, new Set([...seen, page.id])),
      }));
  return build(null, 0, new Set());
}

/** The tree flattened in display order, skipping the children of collapsed pages. */
export function visibleDocNodes<T extends DocPageSummary>(tree: DocNode<T>[], collapsed: Set<string>): DocNode<T>[] {
  const out: DocNode<T>[] = [];
  const walk = (nodes: DocNode<T>[]) => {
    for (const node of nodes) {
      out.push(node);
      if (!collapsed.has(node.page.id)) walk(node.children);
    }
  };
  walk(tree);
  return out;
}

export type FormatKind = "bold" | "italic" | "code" | "link" | "h2" | "h3" | "bullet" | "number" | "task" | "quote";
export type Edit = { value: string; start: number; end: number };

const LINE_PREFIX: Partial<Record<FormatKind, string>> = {
  h2: "## ",
  h3: "### ",
  bullet: "- ",
  number: "1. ",
  task: "- [ ] ",
  quote: "> ",
};

/** Applies a toolbar action to the selection `[start, end)` of `value`. */
export function applyFormat(value: string, start: number, end: number, kind: FormatKind): Edit {
  const prefix = LINE_PREFIX[kind];
  if (prefix) {
    // Every line the selection touches gets the prefix, once.
    const from = value.lastIndexOf("\n", start - 1) + 1;
    const nl = value.indexOf("\n", end);
    const to = nl === -1 ? value.length : nl;
    const lines = value.slice(from, to).split("\n");
    const next = lines.map((l) => (l.startsWith(prefix) ? l : prefix + l)).join("\n");
    return { value: value.slice(0, from) + next + value.slice(to), start: from, end: from + next.length };
  }
  const selected = value.slice(start, end);
  if (kind === "link") {
    const text = selected || "link text";
    const inserted = `[${text}](https://)`;
    const urlStart = start + text.length + 3;
    return {
      value: value.slice(0, start) + inserted + value.slice(end),
      start: urlStart,
      end: urlStart + "https://".length,
    };
  }
  const mark = kind === "bold" ? "**" : kind === "italic" ? "*" : "`";
  const text = selected || (kind === "code" ? "code" : "text");
  return {
    value: value.slice(0, start) + mark + text + mark + value.slice(end),
    start: start + mark.length,
    end: start + mark.length + text.length,
  };
}
